import React, { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { typesService, ModelReferenceCreate, ModelReferenceUpdate } from '../../services/typesService';
import SearchableSelect from '../common/SearchableSelect';
import { SortableHeader } from '../common/SortableHeader';
import { ListEmpty, ListErrorCard, ListLoadingCard } from '../common/ListStateCard';
import { AddModelReferenceModal } from './AddModelReferenceModal';
import { EditModelReferenceModal } from './EditModelReferenceModal';
import { GLOBAL_CLIENT, ModelRefWithHardware, RefSortField, useModelReferencesList } from './modelReferencesList/useModelReferencesList';

interface ModelReferencesTableProps {
  canEdit?: boolean;
  refreshTrigger?: number;
}

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

/**
 * The model references of the catalogue: a toolbar (search, type filter,
 * inactive switch, figures, add), one row per reference with its type and
 * clients, edit and delete. The data lives in `useModelReferencesList`.
 */
export const ModelReferencesTable: React.FC<ModelReferencesTableProps> = ({ canEdit = false, refreshTrigger }) => {
  const { formatDate, formatNumber, t } = useI18n();
  const list = useModelReferencesList(refreshTrigger);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState<ModelRefWithHardware | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const sortHeader = (field: RefSortField, label: string) => (
    <SortableHeader field={field} label={label} activeField={list.sort.field} direction={list.sort.direction} onSort={list.sortBy} />
  );

  const deleteRef = async (ref: ModelRefWithHardware) => {
    if (!window.confirm(t('deviceTypes.deleteRefConfirm', { code: ref.code }))) return;
    setActionError(null);
    try {
      await typesService.deleteModelReference(ref.id);
      list.reload();
    } catch (err: unknown) {
      setActionError(t('deviceTypes.deleteRefFailed', { error: err instanceof Error ? err.message : String(err) }));
    }
  };

  /** The clients column: Global, one name, or the first name with a +N. */
  const clientsCell = (ref: ModelRefWithHardware): React.ReactNode => {
    const ids = ref.client_ids || [GLOBAL_CLIENT];
    if (ids.length === 0) return <span className="tpl-muted">–</span>;
    const first = ids[0] === GLOBAL_CLIENT ? <span className="tpl-owner-anep">{t('deviceTypes.modelRef.global')}</span> : <span>{list.clientName(ids[0])}</span>;
    if (ids.length === 1) return first;
    return <span className="dt-clients" title={ids.map(list.clientName).join(', ')}>{first} <span className="dt-ref-more">+{ids.length - 1}</span></span>;
  };

  if (list.loading) return <ListLoadingCard message={t('deviceTypes.loadingModelRefs')} />;
  if (list.error) return <ListErrorCard title={t('deviceTypes.errorLoadingModelRefs')} message={list.error} onRetry={list.reload} />;

  const { facts } = list;
  const addButton = (testId: string) => (
    <button type="button" className="dt-add" data-testid={testId} onClick={() => setShowAddModal(true)}>
      <span aria-hidden="true">+</span> {t('deviceTypes.addModelReference')}
    </button>
  );

  return (
    <div className="tpl-container model-refs-container">
      <div className="tpl-card">
        <div className="tpl-toolbar">
          <input
            type="search"
            data-testid="field-model-refs-search"
            placeholder={t('deviceTypes.searchModelRefs')}
            value={list.searchTerm}
            onChange={(e) => list.setSearchTerm(e.target.value)}
            className="tpl-search"
            aria-label={t('deviceTypes.searchModelRefs')}
          />
          <SearchableSelect
            className="tpl-select dt-type-filter"
            data-testid="filter-model-refs-hardware-type"
            ariaLabel={t('deviceTypes.allHardwareTypes')}
            value={list.filterHardwareType === 'all' ? '' : String(list.filterHardwareType)}
            onChange={(next) => list.setFilterHardwareType(next === '' ? 'all' : Number(next))}
            placeholder={t('deviceTypes.allHardwareTypes')}
            options={list.hardwareTypes.map((type) => ({ value: String(type.id), label: `${type.name} (${type.code})` }))}
          />
          {list.canSeeInactive && (
            <label className="tpl-check" title={t('common.showInactiveHint')}>
              <input type="checkbox" checked={list.showInactive} onChange={(e) => list.setShowInactive(e.target.checked)} />
              {t('common.showInactive')}
            </label>
          )}
          <div className="tpl-facts" data-testid="model-refs-facts">
            <span><strong>{formatNumber(facts.total)}</strong> {t('deviceTypes.facts.refs')}</span>
            <span className="is-ok"><strong>{formatNumber(facts.active)}</strong> {t('deviceTypes.facts.active')}</span>
            {facts.inactive > 0 && <span className="is-warning"><strong>{formatNumber(facts.inactive)}</strong> {t('deviceTypes.facts.inactive')}</span>}
            <span><strong>{formatNumber(facts.typesWithRefs)}</strong> {t('deviceTypes.facts.typesWithRefs')}</span>
          </div>
          {canEdit && addButton('btn-add-model-reference')}
        </div>

        {actionError && (
          <div className="error-message dt-action-error" role="alert">
            <span>{actionError}</span>
            <button type="button" className="tpl-link" onClick={() => setActionError(null)} aria-label={t('common.dismissError')}>×</button>
          </div>
        )}

        <div className="tpl-table-wrap">
          <table className="tpl-table" data-testid="model-refs-table">
            <thead>
              <tr>
                {sortHeader('code', t('deviceTypes.columns.code'))}
                {sortHeader('name', t('deviceTypes.columns.name'))}
                {sortHeader('hardware_type_code', t('deviceTypes.columns.hardwareType'))}
                <th>{t('deviceTypes.columns.clients')}</th>
                {list.showInactive && <th>{t('deviceTypes.columns.status')}</th>}
                {sortHeader('updated_at', t('deviceTypes.columns.updated'))}
                {canEdit && <th className="dt-col-actions"><span className="sr-only">{t('deviceTypes.columns.actions')}</span></th>}
              </tr>
            </thead>
            <tbody>
              {list.rows.map((ref) => (
                <tr key={ref.id} className={`tpl-row dt-ref-row${ref.is_active ? '' : ' is-off'}`} data-testid={`row-model-ref-${ref.id}`} onClick={() => canEdit && setEditing(ref)}>
                  <td><span className="type-badge tpl-type">{ref.code}</span></td>
                  <td className="tpl-cell-name">
                    <strong className="tpl-name">{ref.name || <span className="tpl-muted">–</span>}</strong>
                    {ref.description && <span className="tpl-sub">{ref.description}</span>}
                  </td>
                  <td><span className="dt-hw"><span className="type-badge tpl-type">{ref.hardware_type_code}</span><span className="dt-hw-name">{ref.hardware_type_name}</span></span></td>
                  <td>{clientsCell(ref)}</td>
                  {list.showInactive && (
                    <td>
                      <span className={`status-badge ${ref.is_active ? 'active' : 'inactive'}`}>{ref.is_active ? t('common.active') : t('common.inactive')}</span>
                    </td>
                  )}
                  <td className="tpl-muted">{formatDate(ref.updated_at)}</td>
                  {canEdit && (
                    <td className="dt-col-actions" onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="dt-icon-btn" onClick={() => setEditing(ref)} title={t('common.edit')} aria-label={t('common.edit')} data-testid={`btn-edit-model-ref-${ref.id}`}><EditIcon /></button>
                      <button type="button" className="dt-icon-btn is-danger" onClick={() => deleteRef(ref)} title={t('common.delete')} aria-label={t('common.delete')} data-testid={`btn-delete-model-ref-${ref.id}`}><TrashIcon /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {list.rows.length === 0 && (
            <ListEmpty title={t('deviceTypes.noModelReferencesFound')} hint={list.hasFilters ? t('deviceTypes.emptyHint') : undefined}>
              {!list.hasFilters && canEdit && addButton('btn-add-model-reference-empty')}
            </ListEmpty>
          )}
        </div>
      </div>

      <AddModelReferenceModal
        isOpen={showAddModal}
        hardwareTypes={list.hardwareTypes}
        onClose={() => setShowAddModal(false)}
        onCreated={list.reload}
        onCreate={(hardwareTypeId: number, data: ModelReferenceCreate) => typesService.createModelReference(hardwareTypeId, data).then(() => undefined)}
      />

      <EditModelReferenceModal
        isOpen={editing !== null}
        modelRef={editing}
        onClose={() => setEditing(null)}
        onUpdated={list.reload}
        onUpdate={(id: number, data: ModelReferenceUpdate) => typesService.updateModelReference(id, data).then(() => undefined)}
      />
    </div>
  );
};
